import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Icon from "aldoo-ra/Icon"
import Value from "aldoo-ra/Value"
import Tabs from "aldoo-ra/Tabs"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { PencilIcon, ClipboardIcon, TrashIcon, ArrowRightIcon, ArrowPathIcon, XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { UpdateParam, CreateParam, RenameParam, ValidateAddress, GetValueType, FormatValue, ParseValue, ClearParamHistory, DeleteParam, GetParamHistory, RemoveHistoryItem, GetParamTags } from "../api"

const ParamEditDialog = ({ param = null, onClose, mode = "edit", onRefresh = null }) => {
  const [userProfile] = Value("user-profile")
  const [formData, setFormData] = useState({
    address: "",
    value: "",
    description: "",
    author: "admin"
  })
  const [valueType, setValueType] = useState("string")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [addressError, setAddressError] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [editingDescription, setEditingDescription] = useState(false)
  const [tempDescription, setTempDescription] = useState("")
  const [editingAuthor, setEditingAuthor] = useState(false)
  const [tempAuthor, setTempAuthor] = useState("")
  const [revertDialog, setRevertDialog] = useState({ show: false, entry: null })
  const [refreshingHistory, setRefreshingHistory] = useState(false)
  const [historySearch, setHistorySearch] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const [activeTab, setActiveTab] = useState("value")
  
  // Title editing state
  const [editingTitle, setEditingTitle] = useState(false)
  const [tempTitle, setTempTitle] = useState("")
  
  // Tag management state
  const [paramTags, setParamTags] = useState([])
  const [availableTags, setAvailableTags] = useState([])
  const [newTag, setNewTag] = useState("")
  const [loadingTags, setLoadingTags] = useState(false)

  // Determine if we're in create mode
  const isCreateMode = mode === "create" || !param

  // Current value type (read-only in edit mode, selectable in create mode)
  const currentType = isCreateMode ? valueType : GetValueType(param?.value)

  // Value type options for create mode
  const typeOptions = [
    { value: "string", label: "String" },
    { value: "number", label: "Number" },
    { value: "boolean", label: "Boolean" },
    { value: "object", label: "Object/JSON" },
    { value: "array", label: "Array/JSON" }
  ]

  // Initialize form with parameter data or defaults for create mode
  useEffect(() => {
    if (isCreateMode) {
      // Create mode - start with empty form
      let authorValue = "admin"
      if (userProfile?.email) {
        const authorName = userProfile.name || userProfile.email.split('@')[0]
        authorValue = `${authorName} ${userProfile.email}`
      }

      setFormData({
        address: "",
        value: "",
        description: "",
        author: authorValue
      })
      setParamTags([]) // Initialize empty tags for create mode
    } else if (param) {
      // Edit mode - populate with parameter data
      const formattedValue = FormatValue(param.value)
      let authorValue = "admin"

      if (userProfile?.email) {
        const authorName = userProfile.name || userProfile.email.split('@')[0]
        authorValue = `${authorName} ${userProfile.email}`
      }

      setFormData({
        address: param.address || "",
        value: currentType === "boolean" ? param.value.toString() : formattedValue,
        description: param.description || "",
        author: authorValue
      })
      setParamTags(param.tags || []) // Initialize with existing tags
    }
  }, [param, currentType, userProfile, isCreateMode])

  // Auto-set author from user profile when it becomes available
  useEffect(() => {
    if (userProfile?.email) {
      const authorName = userProfile.name || userProfile.email.split('@')[0]
      const authorFormat = `${authorName} ${userProfile.email}`
      setFormData(prev => ({ ...prev, author: authorFormat }))
    }
  }, [userProfile])

  // Load available tags
  useEffect(() => {
    const loadAvailableTags = async () => {
      try {
        setLoadingTags(true)
        const response = await GetParamTags()
        if (response.result && response.data) {
          setAvailableTags(response.data)
        }
      } catch (error) {
        console.error('Failed to load available tags:', error)
      } finally {
        setLoadingTags(false)
      }
    }

    loadAvailableTags()
  }, [])

  // Auto-fetch history when history tab is opened
  useEffect(() => {
    if (activeTab === "history" && !isCreateMode && param?.address) {
      // Only fetch if we don't already have history or if it's been a while
      if (!param.history || param.history.length === 0) {
        fetchHistory(false) // Auto-fetch without notifications
      }
    }
  }, [activeTab, isCreateMode, param?.address])

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)

    // Clear general errors when any field changes
    if (error) {
      setError(null)
    }
    
    // Clear address error when address field changes in create mode
    if (field === "address" && isCreateMode && addressError) {
      setAddressError(null)
    }
  }

  // Handle description editing
  const startEditingDescription = () => {
    setTempDescription(formData.description)
    setEditingDescription(true)
  }

  const applyDescriptionChange = () => {
    setFormData(prev => ({ ...prev, description: tempDescription }))
    setEditingDescription(false)
    setHasChanges(true)
  }

  const cancelDescriptionEdit = () => {
    setTempDescription("")
    setEditingDescription(false)
  }

  // Handle author editing
  const startEditingAuthor = () => {
    setTempAuthor(formData.author)
    setEditingAuthor(true)
  }

  const applyAuthorChange = () => {
    setFormData(prev => ({ ...prev, author: tempAuthor }))
    setEditingAuthor(false)
    setHasChanges(true)
  }

  const cancelAuthorEdit = () => {
    setTempAuthor("")
    setEditingAuthor(false)
  }

  // Handle title editing
  const startEditingTitle = () => {
    setTempTitle(formData.address || param?.address || "")
    setEditingTitle(true)
  }

  const applyTitleChange = async () => {
    const currentAddress = formData.address || param?.address
    const newAddress = tempTitle.trim()
    
    if (newAddress && newAddress !== currentAddress) {
      try {
        setLoading(true)
        
        // In edit mode, use rename API immediately
        if (!isCreateMode && param?.address) {
          const renameData = {
            old_address: param.address,
            new_address: newAddress,
            author: formData.author.trim() || "admin"
          }
          
          const response = await RenameParam(renameData)
          
          if (response.result) {
            // Update the param object with the new address
            param.address = newAddress
            setFormData(prev => ({ ...prev, address: newAddress }))
            
            // Trigger DataTable refresh
            if (onRefresh) {
              onRefresh()
            }
            
            showNotification({
              message: "Parameter renamed successfully",
              className: "bg-admin_success text-white",
              duration: 2000
            })
          } else {
            showNotification({
              message: response.error || "Failed to rename parameter",
              className: "bg-admin_error text-white",
              duration: 3000
            })
            return // Don't close editing if rename failed
          }
        } else {
          // In create mode, just update form data
          setFormData(prev => ({ ...prev, address: newAddress }))
          setHasChanges(true)
        }
      } catch (err) {
        showNotification({
          message: "Error renaming parameter",
          className: "bg-admin_error text-white",
          duration: 3000
        })
        return // Don't close editing if error occurred
      } finally {
        setLoading(false)
      }
    }
    
    setEditingTitle(false)
    setTempTitle("")
  }

  const cancelTitleEdit = () => {
    setTempTitle("")
    setEditingTitle(false)
  }

  // Handle revert to historical value
  const handleRevert = (entry) => {
    // Set the form value to the historical value
    const formattedValue = currentType === "boolean" ? entry.value.toString() : FormatValue(entry.value)
    setFormData(prev => ({ ...prev, value: formattedValue }))
    setHasChanges(true)
    setRevertDialog({ show: false, entry: null })

    showNotification({
      message: "Value reverted. Click Update to save changes.",
      className: "bg-admin_success text-white",
      duration: 3000
    })
  }

  // Fetch history data (internal function)
  const fetchHistory = async (showNotifications = true) => {
    try {
      setRefreshingHistory(true)
      const response = await GetParamHistory({ address: param.address })
      if (response.result && response.data) {
        // Update the param object with new history data from the nested structure
        param.history = response.data.history || []
        // Force a re-render by updating a state that triggers the component to refresh
        setRefreshKey(Date.now())
        
        if (showNotifications) {
          const historyCount = response.data.historyCount || response.data.history?.length || 0
          showNotification({
            message: `History refreshed - ${historyCount} entries loaded`,
            className: "bg-admin_success text-white",
            duration: 2000
          })
        }
        return true
      } else {
        if (showNotifications) {
          showNotification({
            message: response.error || "Failed to refresh history",
            className: "bg-admin_error text-white",
            duration: 3000
          })
        }
        return false
      }
    } catch (err) {
      if (showNotifications) {
        showNotification({
          message: "Error refreshing history",
          className: "bg-admin_error text-white",
          duration: 3000
        })
      }
      return false
    } finally {
      setRefreshingHistory(false)
    }
  }

  // Handle refresh history (button click)
  const handleRefreshHistory = async () => {
    await fetchHistory(true) // Show notifications for manual refresh
  }

  // Handle parameter deletion
  const handleDelete = () => {
    showPromptDialog({
      title: "Delete Parameter",
      message: `Are you sure you want to delete the parameter "${param.address}"? This action cannot be undone.`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          setLoading(true)
          const response = await DeleteParam({ address: param.address })
          if (response.result) {
            showNotification({
              message: "Parameter deleted successfully",
              className: "bg-admin_success text-white",
              duration: 2000
            })
            onClose(true) // Close with refresh
          } else {
            showNotification({
              message: response.error || "Failed to delete parameter",
              className: "bg-admin_error text-white",
              duration: 3000
            })
          }
        } catch (err) {
          showNotification({
            message: "Error deleting parameter",
            className: "bg-admin_error text-white",
            duration: 3000
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  // Handle history item deletion
  const handleDeleteHistoryItem = (entry) => {
    showPromptDialog({
      title: "Delete History Entry",
      message: `Are you sure you want to delete this history entry? This action cannot be undone.`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const response = await RemoveHistoryItem({ address: param.address, hash: entry.hash })
          if (response.result) {
            // Remove the entry from the current history array
            param.history = param.history.filter(h => h.hash !== entry.hash)
            // Force a re-render
            setRefreshKey(Date.now())
            showNotification({
              message: "History entry deleted successfully",
              className: "bg-admin_success text-white",
              duration: 2000
            })
          } else {
            showNotification({
              message: response.error || "Failed to delete history entry",
              className: "bg-admin_error text-white",
              duration: 3000
            })
          }
        } catch (err) {
          showNotification({
            message: "Error deleting history entry",
            className: "bg-admin_error text-white",
            duration: 3000
          })
        }
      }
    })
  }

  // Copy value to clipboard
  const handleCopyValue = async () => {
    try {
      await navigator.clipboard.writeText(formData.value)
      showNotification({
        message: (
          <div className="flex items-center space-x-2">
            <ClipboardIcon className="w-4 h-4" />
            <span>Value copied to clipboard</span>
          </div>
        ),
        className: "bg-admin_success text-white",
        duration: 2000
      })
    } catch (err) {
      showNotification({
        message: "Failed to copy value to clipboard",
        className: "bg-admin_error text-white",
        duration: 3000
      })
    }
  }

  // Tag management functions
  const handleAddTag = () => {
    if (!newTag.trim()) return

    const trimmedTag = newTag.trim()
    if (!paramTags.includes(trimmedTag)) {
      setParamTags([...paramTags, trimmedTag])
      setNewTag("")
      setHasChanges(true)
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setParamTags(paramTags.filter(tag => tag !== tagToRemove))
    setHasChanges(true)
  }

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddTag()
    }
  }

  const handleTagSelect = (selectedOptions) => {
    const selectedTags = selectedOptions.map(option => option.value)
    setParamTags(selectedTags)
    setHasChanges(true)
  }


  // Helper function to get unique historical authors
  const getHistoricalAuthors = () => {
    if (!param.history || !Array.isArray(param.history)) {
      return []
    }

    const currentAuthor = formData.author || "Unknown"
    const uniqueAuthors = [...new Set(
      param.history
        .map(entry => entry.author)
        .filter(author => author && author !== currentAuthor)
    )]

    return uniqueAuthors
  }

  // Filter history items based on search
  const getFilteredHistory = () => {
    if (!param.history || !Array.isArray(param.history)) {
      return []
    }

    if (!historySearch.trim()) {
      return param.history
    }

    const searchLower = historySearch.toLowerCase().trim()
    return param.history.filter(entry => {
      const formattedValue = FormatValue(entry.value).toLowerCase()
      return formattedValue.includes(searchLower)
    })
  }

  // Force component to re-render when refreshKey changes
  useEffect(() => {
    // This effect ensures the component re-renders when history is refreshed
  }, [refreshKey])

  // Parse author to separate name and email
  const parseAuthor = (authorString) => {
    if (!authorString || authorString === "Unknown") {
      return { name: "Unknown", email: null }
    }

    const parts = authorString.split(' ')
    const emailPattern = /\S+@\S+\.\S+/
    const emailIndex = parts.findIndex(part => emailPattern.test(part))

    if (emailIndex !== -1) {
      const name = parts.slice(0, emailIndex).join(' ')
      const email = parts[emailIndex]
      return { name: name || email.split('@')[0], email }
    }

    return { name: authorString, email: null }
  }

  // Handle boolean toggle for boolean type
  const handleBooleanChange = (value) => {
    setFormData(prev => ({ ...prev, value: value.toString() }))
    setHasChanges(true)
    
    // Clear general errors when value changes
    if (error) {
      setError(null)
    }
  }

  // Helper function to determine if a field has an error
  const hasFieldError = (field) => {
    if (field === "address") {
      return !!addressError || (error && error.toLowerCase().includes("address"))
    }
    if (field === "value") {
      return error && (error.toLowerCase().includes("value") || error.toLowerCase().includes("required"))
    }
    return false
  }

  // Handle form submission
  const handleSubmit = async () => {
    // Validation for create mode
    if (isCreateMode) {
      if (!formData.address.trim()) {
        setError("Address is required")
        return
      }

      if (!ValidateAddress(formData.address)) {
        setError("Invalid address format")
        return
      }
    }

    if (currentType !== "boolean" && !formData.value.trim()) {
      setError("Value is required")
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Parse value based on type
      let parsedValue
      try {
        if (currentType === "boolean") {
          parsedValue = formData.value === "true"
        } else {
          // Trim string values before parsing
          const trimmedValue = currentType === "string" ? formData.value.trim() : formData.value
          parsedValue = ParseValue(trimmedValue, currentType)
        }
      } catch (parseError) {
        setError(`Invalid ${currentType} format: ${parseError.message}`)
        return
      }

      // Create parameter data
      const paramData = {
        address: formData.address.trim(),
        value: parsedValue,
        description: formData.description.trim() || undefined,
        author: formData.author.trim() || "admin",
        tags: paramTags
      }

      // Call appropriate API
      const response = isCreateMode
        ? await CreateParam(paramData)
        : await UpdateParam(paramData)

      if (response.result) {
        onClose(true) // Close with refresh
      } else {
        setError(response.error || (isCreateMode ? "Failed to create parameter" : "Failed to update parameter"))
      }
    } catch (err) {
      setError(err.message || (isCreateMode ? "An error occurred while creating parameter" : "An error occurred while updating parameter"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      variant="admin_centered"
      title={isCreateMode ? "Create New Parameter" : "Edit Parameter"}
      isOpen={true}
      onClose={() => onClose(false)}
      className="max-w-[50vw] h-[75vh] min-h-[600px]"
      footerContent={
        <div className="flex flex-col w-full">
          {/* Error Messages Row */}
          {(error || addressError) && (
            <div className="border-b border-admin_bordered bg-admin_error px-6 py-2">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-white flex-shrink-0" />

                <Typography 
                  variant="admin_txt2" 
                  className="text-white text-sm"
                >
                  {error || addressError}
                </Typography>
              </div>
            </div>
          )}
          
          {/* Main Footer Content */}
          <div className="w-full flex items-center justify-between gap-6 py-4">
            {/* Left side: Delete Button and Date Chips (only in edit mode) */}
            <div className="flex items-center space-x-3">
              {!isCreateMode && (
                <>
                  <Button
                    variant="admin_error"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    <TrashIcon className="w-5 h-5 mr-2" />
                    Delete
                  </Button>

                  <div className="flex items-center space-x-2">
                    <Typography 
                      variant="admin_txt2_dim"
                      className="border border-admin_bordered rounded px-2 py-1"
                    >
                      Created:{" "}
                      {param?.created
                        ? new Date(param.created).toLocaleDateString()
                        : "Unknown"}
                    </Typography>

                    <Typography 
                      variant="admin_txt2_dim"
                      className="border border-admin_bordered rounded px-2 py-1"
                    >
                      Updated:{" "}
                      {param?.updated
                        ? new Date(param.updated).toLocaleDateString()
                        : "Never"}
                    </Typography>
                  </div>
                </>
              )}
            </div>

            {/* Right side: Action buttons */}
            <div className="flex items-center space-x-3">
              <Button
                variant="admin_secondary"
                onClick={() => onClose(false)}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="admin_primary"
                onClick={handleSubmit}
                disabled={
                  loading || (isCreateMode ? !!addressError : !hasChanges)
                }
                className="gap-2"
              >
                {loading && (
                  <Icon name="spinner" className="w-4 h-4 animate-spin" />
                )}

                {isCreateMode ? "Create Parameter" : "Update"}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        {/* Inline Author Editing */}
        {editingAuthor && (
          <div className="border border-admin_bordered rounded-lg p-4 mb-6">
            <Typography
              variant="admin_label"
            >
              Edit Author
            </Typography>

            <div className="flex items-center space-x-2">
              <Input
                variant="admin"
                placeholder="Author name and email"
                value={tempAuthor}
                onChange={(e) => setTempAuthor(e.target.value)}
                className="flex-1"
                autoFocus
              />

              <Button
                variant="admin_secondary"
                onClick={cancelAuthorEdit}
              >
                Cancel
              </Button>

              <Button
                variant="admin_secondary"
                onClick={applyAuthorChange}
              >
                Apply
              </Button>
            </div>
          </div>
        )}

        {/* Parameter Header with Address and Type */}
        {isCreateMode ? (
          <div className="space-y-4 mb-6">
            {/* Address Input for Create Mode */}
            <div>
              <Typography
                variant="admin_label"
              >
                Parameter Address *
              </Typography>

              <Input
                variant="admin"
                placeholder="e.g., database.host, api.timeout, features.beta.enabled"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                error={hasFieldError("address")}
                className="w-full"
              />

              {addressError && (
                <Typography
                  variant="admin_txt2"
                  className="text-admin_error mt-1"
                >
                  {addressError}
                </Typography>
              )}
              
              <Typography
                variant="admin_txt2_dim"
                className="mt-1"
              >
                Use dots to create hierarchical structure (e.g.,
                "api.endpoints.timeout")
              </Typography>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {!editingTitle ? (
                  <>
                    <Typography
                      variant="admin_h4"
                    >
                      {formData.address || param?.address}
                    </Typography>
                    <Button
                      variant="admin_icon"
                      onClick={startEditingTitle}
                      title="Edit parameter address"
                      className="p-1 hover:bg-admin_neutral/20 rounded"
                    >
                      <PencilIcon className="w-5 h-5 text-admin_primary hover:text-admin_primary_dark" />
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center space-x-2 mt-4 mb-2">
                    <Input
                      variant="admin"
                      value={tempTitle}
                      onChange={(e) => setTempTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          applyTitleChange()
                        } else if (e.key === "Escape") {
                          cancelTitleEdit()
                        }
                      }}
                      className="text-lg font-semibold min-w-[400px] max-w-[600px]"
                      autoFocus
                    />
                    <Button
                      variant="admin_secondary"
                      onClick={applyTitleChange}
                      className="px-2 py-1 text-xs"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      variant="admin_secondary"
                      onClick={cancelTitleEdit}
                      className="px-2 py-1 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  currentType === "string"
                    ? "bg-admin_success text-admin_text_inv"
                    : currentType === "number"
                    ? "bg-admin_info_bg text-admin_text"
                    : currentType === "boolean"
                    ? "bg-admin_progress text-admin_text_inv"
                    : currentType === "object"
                    ? "bg-admin_warning text-white"
                    : currentType === "array"
                    ? "bg-admin_primary/50 text-admin_text_inv"
                    : "bg-admin_neutral_1 text-admin_text"
                }`}
              >
                {currentType}
              </span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex-1 flex flex-col min-h-0">
          <Tabs
            variant="admin"
            value={activeTab}
            onChange={setActiveTab}
            tabs={
              isCreateMode
                ? [
                    { value: "value", label: "Value" },
                    { value: "description", label: "Description" },
                    { value: "author", label: "Author" },
                    { value: "tags", label: "Tags" },
                  ]
                : [
                    { value: "value", label: "Value" },
                    { value: "description", label: "Description" },
                    { value: "author", label: "Author" },
                    { value: "tags", label: "Tags" },
                    { value: "history", label: "History" },
                  ]
            }
            className="mb-4"
          />

          <div className="flex-1 flex flex-col min-h-0">
            {/* Value Tab */}
           
            {activeTab === "value" && (
              <div className="flex-1 overflow-y-auto space-y-6 p-4">
                {/* Value Type Selection (only in create mode) */}
                {isCreateMode && (
                  <div>
                    <Typography
                      variant="admin_label"
                    >
                      Value Type *
                    </Typography>

                    <DropDownSelect
                      variant="admin"
                      options={typeOptions}
                      value={[
                        {
                          value: valueType,
                          label: typeOptions.find(
                            (opt) => opt.value === valueType
                          )?.label,
                        },
                      ]}
                      onChange={(selected) => {
                        const newType = selected[0]?.value || "string"
                        setValueType(newType)
                        // Reset value when type changes
                        setFormData((prev) => ({
                          ...prev,
                          value: newType === "boolean" ? "false" : "",
                        }))
                        setHasChanges(true)
                      }}
                      className="w-full"
                    />
                  </div>
                )}

                <div>
                  <Typography
                    variant="admin_label"
                  >
                    Value *
                  </Typography>

                  {currentType === "boolean" ? (
                    <div className={`flex items-center space-x-3 p-3 rounded-md border ${
                      hasFieldError("value") 
                        ? "border-admin_error bg-admin_neutral_1/50" 
                        : "border-transparent"
                    }`}>
                      <ToggleSwitch
                        variant="admin"
                        isOn={formData.value === "true"}
                        onChange={handleBooleanChange}
                      />

                      <Typography
                        variant="admin_txt1"
                        className="text-admin_text"
                      >
                        {formData.value === "true" ? "True" : "False"}
                      </Typography>
                    </div>
                  ) : currentType === "object" || currentType === "array" ? (
                    <div>
                      <Input
                        variant="admin"
                        multiline
                        rows={8}
                        placeholder={
                          currentType === "object"
                            ? '{"key": "value"}'
                            : '["item1", "item2"]'
                        }
                        value={formData.value}
                        onChange={(e) =>
                          handleInputChange("value", e.target.value)
                        }
                        error={hasFieldError("value")}
                        className="w-full font-mono text-sm"
                      />

                      <Typography
                        variant="admin_txt2_dim"
                        className="mt-1"
                      >
                        Enter valid JSON format
                      </Typography>
                    </div>
                  ) : (
                    <div>
                      {currentType === "string" && formData.value.length > 100 ? (
                        <div>
                          <Input
                            variant="admin"
                            multiline
                            rows={4}
                            placeholder="Enter string value"
                            value={formData.value}
                            onChange={(e) =>
                              handleInputChange("value", e.target.value)
                            }
                            error={hasFieldError("value")}
                            className="w-full min-h-[120px]"
                          />

                          <div className="flex justify-start">
                            <Button
                              variant="admin_icon"
                              onClick={handleCopyValue}
                              title="Copy value"
                              className="gap-1"
                            >
                              <ClipboardIcon className="w-4 h-4"/>
                              Copy
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <Input
                            variant="admin"
                            type={currentType === "number" ? "number" : "text"}
                            placeholder={
                              currentType === "string"
                                ? "Enter string value"
                                : currentType === "number"
                                ? "Enter number value"
                                : "Enter value"
                            }
                            value={formData.value}
                            onChange={(e) =>
                              handleInputChange("value", e.target.value)
                            }
                            error={hasFieldError("value")}
                            className="flex-1"
                          />
                          <Button
                            variant="admin_icon"
                            onClick={handleCopyValue}
                            title="Copy value"
                          >
                            <ClipboardIcon className="w-4 h-4 text-admin_text_secondary hover:text-admin_primary" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Copy Value Button for Boolean and Complex Types */}
                {(currentType === "boolean" ||
                  currentType === "object" ||
                  currentType === "array") && (
                  <div className="flex justify-start">
                    <Button
                      variant="admin_secondary"
                      onClick={handleCopyValue}
                      className="gap-2"
                    >
                      <ClipboardIcon className="w-4 h-4" />
                      Copy
                    </Button>
                  </div>
                )}

                {/* Type Information */}
                <div className="border border-admin_bordered rounded-lg p-3">
                  <Typography
                    variant="admin_label"
                  >
                    Current Type
                  </Typography>

                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium mr-2 ${
                      currentType === "string"
                        ? "bg-admin_success text-admin_text_inv"
                        : currentType === "number"
                        ? "bg-admin_info_bg text-admin_text"
                        : currentType === "boolean"
                        ? "bg-admin_progress text-admin_text_inv"
                        : currentType === "object"
                        ? "bg-admin_warning text-white"
                        : currentType === "array"
                        ? "bg-admin_primary/50 text-admin_text_inv"
                        : "bg-admin_neutral_1 text-admin_text"
                    }`}
                  >
                    {currentType}
                  </span>

                  <Typography
                    variant="admin_txt2_dim"
                    className="mt-2"
                  >
                    {currentType === "string" &&
                      "Text values can contain any characters"}
                    {currentType === "number" &&
                      "Numeric values (integers or decimals)"}
                    {currentType === "boolean" && "True or false values"}
                    {currentType === "object" &&
                      "JSON objects with key-value pairs"}
                    {currentType === "array" &&
                      "JSON arrays with ordered elements"}
                  </Typography>
                </div>
              </div>
            )}

            {/* Description Tab */}
            {activeTab === "description" && (
              <div className="flex-1 overflow-y-auto space-y-4 px-4">
                <div>
                  <Typography
                    variant="admin_label"
                  >
                    Description
                  </Typography>

                  {!editingDescription ? (
                    <div
                      className="min-h-[100px] flex items-center space-x-2 border border-admin_bordered rounded-md cursor-pointer transition-colors p-4"
                      onClick={startEditingDescription}
                    >
                      <Typography
                        variant="admin_txt1_dim"
                        className="flex-1"
                      >
                        {formData.description || "No description provided"}
                      </Typography>

                      <PencilIcon className="w-4 h-4 text-admin_text_secondary" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Input
                        variant="admin"
                        multiline
                        rows={6}
                        placeholder="Enter description for this parameter"
                        value={tempDescription}
                        onChange={(e) => setTempDescription(e.target.value)}
                        maxLength={500}
                        autoFocus
                        className="w-full"
                      />

                      <div className="flex items-center justify-between">
                        <Typography
                          variant="admin_txt1_dim"
                        >
                          {tempDescription.length}/500 characters
                        </Typography>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="admin_secondary"
                            onClick={cancelDescriptionEdit}
                          >
                            Cancel
                          </Button>

                          <Button
                            variant="admin_secondary"
                            onClick={applyDescriptionChange}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description Guidelines */}
                <div>
                  <Typography
                    variant="admin_label"
                  >
                    Description Guidelines
                  </Typography>

                  <ul className="space-y-1 text-sm text-admin_text_secondary mb-0">
                    <li>
                      • Keep descriptions clear and concise
                    </li>
                    <li>
                      • Explain the parameter's purpose and usage
                    </li>
                    <li>
                      • Include any important constraints or format requirements
                    </li>
                    <li>
                      • Maximum 500 characters
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Author Tab */}
            {activeTab === "author" && (
              <div className="flex-1 overflow-y-auto space-y-4 p-4 pb-0">
                {/* Current Author */}
                <div className="border border-admin_bordered rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Typography 
                      variant="admin_label"
                    >
                      Current Author
                    </Typography>

                    <Button
                      variant="admin_secondary"
                      onClick={(e) => {
                        e.stopPropagation()
                        startEditingAuthor()
                      }}
                      className="flex items-center gap-2"
                    >
                      <PencilIcon className="w-4 h-4" />
                      Edit
                    </Button>
                  </div>

                  {(() => {
                    const { name, email } = parseAuthor(formData.author)

                    return (
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col text-left">
                          <Typography
                            variant="admin_txt1"
                            className="text-admin_text"
                          >
                            {name}
                          </Typography>

                          {email && (
                            <Typography
                              variant="admin_txt2_dim"
                            >
                              {email}
                            </Typography>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                </div>

                {/* Historical Authors (only in edit mode) */}
                {!isCreateMode &&
                  (() => {
                    const historicalAuthors = getHistoricalAuthors()
                    if (historicalAuthors.length > 0) {
                      return (
                        <div className="border border-admin_bordered rounded-lg p-4">
                          <Typography
                            variant="admin_label"
                          >
                            Previous Authors ({historicalAuthors.length})
                          </Typography>

                          <div className="space-y-2">
                            {historicalAuthors
                              .slice(0, 5)
                              .map((author, index) => {
                                const { name, email } = parseAuthor(author)
                                return (
                                  <div
                                    key={index}
                                    className="flex items-center justify-between p-2 rounded-md hover:bg-neutral"
                                  >
                                    <div className="flex flex-col">
                                      <Typography
                                        variant="admin_txt2"
                                        className="text-admin_text"
                                      >
                                        {name}
                                      </Typography>

                                      {email && (
                                        <Typography variant="admin_txt2_dim">
                                          {email}
                                        </Typography>
                                      )}
                                    </div>

                                    <Button
                                      variant="admin_secondary"
                                      onClick={() => {
                                        setFormData((prev) => ({
                                          ...prev,
                                          author: author,
                                        }))
                                        setHasChanges(true)
                                      }}
                                    >
                                      Use
                                    </Button>
                                  </div>
                                )
                              })}

                            {historicalAuthors.length > 5 && (
                              <Typography
                                variant="admin_txt2_dim"
                                className="text-center pt-2"
                              >
                                +{historicalAuthors.length - 5} more authors
                              </Typography>
                            )}
                          </div>
                        </div>
                      )
                    }
                    return null
                  })(  
                )}

                {/* Author Guidelines */}
                <div className="px-3">
                  <Typography
                    variant="admin_label"
                  >
                    Author Format
                  </Typography>

                  <Typography
                    variant="admin_txt2_dim"
                    className="mb-2"
                  >
                    Use the format:{" "}
                    <span className="font-mono px-1 rounded">
                      Name email@domain.com
                    </span>
                  </Typography>

                  <Typography
                    variant="admin_txt2_dim"
                  >
                    The author field helps track who made changes to this
                    parameter over time.
                  </Typography>
                </div>
              </div>
            )}

            {/* Tags Tab */}
            {activeTab === "tags" && (
              <div className="flex-1 overflow-y-auto p-4">

                {/* Add Tag Input and Dropdown */}
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex-grow relative">
                    <Input
                      variant="admin"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add Custom Tag"
                      className="pr-[65px]"
                    />
                    {newTag.trim() && (
                      <Button
                        variant="admin_primary"
                        onClick={handleAddTag}
                        className="w-[60px] absolute right-[66px] top-1/2 transform -translate-y-1/2 text-xs"
                      >
                        Add
                      </Button>
                    )}
                  </div>

                  <div className="w-48">
                    <DropDownSelect
                      variant="admin"
                      options={availableTags.map(tag => ({ value: tag, label: tag }))}
                      value={paramTags.map(tag => ({ value: tag, label: tag }))}
                      onChange={handleTagSelect}
                      searchEnabled
                      multiSelection={true}
                      placeholder="Select Tag"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Tags Display Area */}
                <div className="min-h-[200px] border border-admin_bordered rounded-md p-4 mb-6">
                  <div className="flex flex-wrap gap-2">
                    {paramTags.map((tag, index) => (
                      <div
                        key={index}
                        className="inline-flex items-center bg-admin_info_bg text-admin_text rounded-full px-3 py-1 border border-admin_bordered shadow-sm"
                      >
                        <Typography variant="admin_txt1" className="text-admin_text">
                          {tag}
                        </Typography>

                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 hover:text-admin_text_secondary transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4 stroke-2" />
                        </button>
                      </div>
                    ))}
                    {paramTags.length === 0 && (
                      <Typography variant="admin_body_dim" className="my-0 text-center w-full py-8 text-admin_text_secondary">
                        No tags added
                      </Typography>
                    )}
                  </div>
                </div>

                {/* Tag Guidelines */}
                <div>
                  <Typography
                    variant="admin_label"
                  >
                    Tag Guidelines
                  </Typography>

                  <ul className="space-y-1 text-sm text-admin_text_secondary mb-0">
                    <li>
                      • Use tags to categorize and organize parameters
                    </li>
                    <li>
                      • Tags help with filtering and searching parameters
                    </li>
                    <li>
                      • Keep tags concise and meaningful
                    </li>
                    <li>
                      • Use existing tags when possible for consistency
                    </li>
                    <li>
                      • Examples: "config", "database", "production", "security"
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* History Tab (only in edit mode) */}
            {!isCreateMode && activeTab === "history" && (
              <div className="flex-1 flex flex-col min-h-0 space-y-4 p-4">
                {/* History Controls */}
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center justify-between">
                    <Typography 
                      variant="admin_label" 
                      className="my-0"
                    >
                      Parameter History (
                      {(() => {
                        const filteredHistory = getFilteredHistory()
                        const totalItems = param.history
                          ? param.history.length
                          : 0
                        const filteredItems = filteredHistory.length
                        return historySearch.trim()
                          ? `${filteredItems}/${totalItems}`
                          : totalItems
                      })()}{" "}
                      items)
                    </Typography>

                    <div className="flex items-center space-x-2">
                      <Button
                        variant="admin_secondary"
                        onClick={handleRefreshHistory}
                        disabled={refreshingHistory}
                        className="flex items-center gap-2"
                      >
                        <ArrowPathIcon
                          className={`w-4 h-4 ${
                            refreshingHistory ? "animate-spin" : ""
                          }`}
                        />
                        Refresh
                      </Button>

                      {param.history && param.history.length > 0 && (
                        <Button
                          variant="admin_secondary"
                          onClick={() => {
                            showPromptDialog({
                              title: "Clear History",
                              message: `Are you sure you want to clear all ${param.history.length} history entries for this parameter? This action cannot be undone.`,
                              confirmText: "Clear History",
                              onConfirm: async () => {
                                try {
                                  const response = await ClearParamHistory({
                                    address: param.address,
                                  })
                                  if (response.result) {
                                    showNotification({
                                      message: "History cleared successfully",
                                      className: "bg-admin_success text-white",
                                      duration: 2000,
                                    })
                                    onClose(true) // Refresh the data
                                  } else {
                                    showNotification({
                                      message:
                                        response.error ||
                                        "Failed to clear history",
                                      className: "bg-admin_error text-white",
                                      duration: 3000,
                                    })
                                  }
                                } catch (err) {
                                  showNotification({
                                    message: "Error clearing history",
                                    className: "bg-admin_error text-white",
                                    duration: 3000,
                                  })
                                }
                              },
                            })
                          }}
                          className="flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Clear All
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Search Controls */}
                  <div className="flex items-center space-x-3">
                    <Input
                      variant="admin"
                      placeholder="Search history values..."
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      showClearButton={true}
                      className="flex-1 max-w-md"
                    />
                    {historySearch.trim() && (
                      <Typography
                        variant="admin_txt2_dim"
                      >
                        {(() => {
                          const filteredHistory = getFilteredHistory()
                          const totalItems = param.history
                            ? param.history.length
                            : 0
                          return `${filteredHistory.length} of ${totalItems} entries match`
                        })()}
                      </Typography>
                    )}
                  </div>
                </div>

                <div className="border border-admin_bordered rounded-lg p-4 flex-1 overflow-y-auto min-h-0">
                  {(() => {
                    const filteredHistory = getFilteredHistory()
                    if (filteredHistory.length === 0) {
                      if (historySearch.trim()) {
                        return (
                          <Typography
                            variant="admin_txt2_dim"
                            className="text-center"
                          >
                            No history entries match "{historySearch}"
                          </Typography>
                        )
                      } else if (param.history && param.history.length > 0) {
                        return (
                          <Typography
                            variant="admin_txt2_dim"
                            className="text-center"
                          >
                            No history available
                          </Typography>
                        )
                      } else {
                        return (
                          <Typography
                            variant="admin_txt2_dim"
                            className="text-center"
                          >
                            No history available
                          </Typography>
                        )
                      }
                    }

                    const displayItems = filteredHistory.slice(0, 5)
                    return (
                      <div className="divide-y divide-admin_bordered">
                        {displayItems.map((entry, index) => (
                          <div
                            key={entry.hash || index}
                            className="flex items-center justify-between text-sm hover:bg-admin_primary_lt/20 rounded-md px-2 transition-colors py-2"
                          >
                            <div className="flex items-center space-x-3 flex-1">
                              <Button
                                variant="admin_secondary"
                                size="xs"
                                onClick={() =>
                                  setRevertDialog({ show: true, entry })
                                }
                                className="text-xs"
                              >
                                Revert
                              </Button>

                              <div className="flex-1">
                                <Typography
                                  variant="admin_txt2"
                                  className="text-admin_text"
                                >
                                  {FormatValue(entry.value).substring(0, 50)}
                                  {FormatValue(entry.value).length > 50
                                    ? "..."
                                    : ""}
                                </Typography>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <div className="flex flex-col text-right">
                                <Typography 
                                  variant="admin_txt2"
                                  className="text-admin_text mb-1"
                                >
                                  {entry.author || "Unknown"}
                                </Typography>

                                <Typography 
                                  variant="admin_txt2_dim"
                                >
                                  {new Date(entry.timestamp).toLocaleString()}
                                </Typography>
                              </div>

                              <Button
                                variant="admin_icon"
                                onClick={() => handleDeleteHistoryItem(entry)}
                                className="flex items-center justify-center w-5 h-5 rounded-full text-admin_error hover:text-white hover:bg-admin_error p-0"
                                title="Delete history entry"
                              >
                                <XMarkIcon className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {filteredHistory.length > 5 && (
                          <div className="pt-2">
                            <Typography
                              variant="admin_txt2_dim"
                              className="text-center"
                            >
                              +{filteredHistory.length - 5} more{" "}
                              {historySearch.trim() ? "matching " : ""}entries
                            </Typography>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revert Confirmation Dialog */}
      {revertDialog.show && revertDialog.entry && (
        <Modal
          variant="admin_centered"
          title={`Revert ${param.address}`}
          isOpen={true}
          onClose={() => setRevertDialog({ show: false, entry: null })}
        >
          <div className="space-y-4">
            <Typography
              variant="admin_body"
              className="my-0"
            >
              Are you sure you want to revert this parameter?
            </Typography>

            <div className="flex items-end space-x-4">
              {/* Current Value */}
              <div className="flex-1">
                <Typography
                  variant="admin_body_dim"
                  className="mt-0 mb-2"
                >
                  Current Value:
                </Typography>

                <div className="bg-admin_canvas border border-admin_bordered rounded-lg p-3">
                  <Typography
                    variant="admin_txt1"
                    className="text-admin_text font-mono break-all"
                  >
                    {FormatValue(param.value)}
                  </Typography>
                </div>
              </div>

              {/* Arrow Icon */}
              <div className="flex-shrink-0">
                <ArrowRightIcon className="w-6 h-6 text-admin_text_secondary" />
              </div>

              {/* New Value (from history) */}
              <div className="flex-1">
                <Typography
                  variant="admin_body_dim"
                  className="mt-0 mb-2"
                >
                  Revert To:
                </Typography>

                <div className="bg-admin_canvas border border-admin_bordered rounded-lg p-3">
                  <Typography
                    variant="admin_txt1"
                    className="text-admin_text font-mono text-sm break-all"
                  >
                    {FormatValue(revertDialog.entry.value)}
                  </Typography>
                </div>
              </div>
            </div>

            <div className="border border-admin_bordered rounded-lg p-3">
              <Typography
                variant="admin_body_dim"
                className="mb-1 mt-0"
              >
                From History Entry:
              </Typography>

              <div className="flex justify-between items-center gap-2">
                  <Typography
                    variant="admin_txt2"
                    className="text-admin_text"
                  >
                    {revertDialog.entry.author || "Unknown"}
                  </Typography>
                  
                  <Typography
                    variant="admin_txt2_dim"
                  >
                    {new Date(revertDialog.entry.timestamp).toLocaleString()}
                  </Typography>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <Button
                variant="admin_secondary"
                onClick={() => setRevertDialog({ show: false, entry: null })}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="admin_primary"
                onClick={() => handleRevert(revertDialog.entry)}
              >
                Revert
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  )
}

export default ParamEditDialog